import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import adminApi from '../../utils/adminApi';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription
} from '../ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import InquiriesTab from './InquiriesTab';
import {
  Package, Ship, LogOut, Plus, Edit, Trash2, RefreshCw, Search,
  Loader2, MapPin, Calendar, Weight, Box, Building2, Hash, User, Clock, MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUSES = [
  { value: 'pending', label: 'Pending / Cargo Received at Warehouse', color: 'bg-yellow-500' },
  { value: 'loaded', label: 'Loaded into Container', color: 'bg-blue-500' },
  { value: 'at_sea', label: 'In Transit / At Sea', color: 'bg-cyan-500' },
  { value: 'at_port', label: 'Arrived at Destination Port', color: 'bg-indigo-500' },
  { value: 'customs', label: 'Under Customs Clearance', color: 'bg-purple-500' },
  { value: 'released', label: 'Customs Cleared & Released', color: 'bg-teal-500' },
  { value: 'delivered', label: 'Delivered to Customer', color: 'bg-green-500' },
  { value: 'delayed', label: 'Delayed', color: 'bg-orange-500' }
];

const AdminDashboard = () => {
  const { token, logout, isAuthenticated } = useAdminAuth();
  const navigate = useNavigate();
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingShipment, setEditingShipment] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const [formData, setFormData] = useState({
    tracking_number: '',
    container_number: '',
    booking_number: '',
    customer_name: '',
    customer_email: '',
    origin: '',
    destination: '',
    carrier: '',
    weight: '',
    volume: '',
    est_departure: '',
    est_arrival: '',
    status: 'pending',
    location: '',
    notes: ''
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin');
      return;
    }
    fetchShipments();
  }, [isAuthenticated]);

  const fetchShipments = async () => {
    setLoading(true);
    try {
      const response = await adminApi.get(`/api/shipments`);
      if (response.data.success) {
        setShipments(response.data.shipments || []);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        logout();
        navigate('/admin');
      } else {
        toast.error('Failed to fetch shipments');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin');
    toast.success('Logged out successfully');
  };

  const resetForm = () => {
    setFormData({
      tracking_number: '', container_number: '', booking_number: '',
      customer_name: '', customer_email: '', origin: '', destination: '',
      carrier: '', weight: '', volume: '', est_departure: '', est_arrival: '',
      status: 'pending', location: '', notes: ''
    });
    setEditingShipment(null);
  };

  const handleOpenDialog = (shipment = null) => {
    if (shipment) {
      setEditingShipment(shipment);
      setFormData({
        tracking_number: shipment.tracking_number || '',
        container_number: shipment.container_number || '',
        booking_number: shipment.booking_number || '',
        customer_name: shipment.customer_name || '',
        customer_email: shipment.customer_email || '',
        origin: shipment.origin || '',
        destination: shipment.destination || '',
        carrier: shipment.carrier || '',
        weight: shipment.weight || '',
        volume: shipment.volume || '',
        est_departure: shipment.est_departure || '',
        est_arrival: shipment.est_arrival || '',
        status: shipment.status || 'pending',
        location: '',
        notes: ''
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Clean form data - remove empty strings
      const cleanData = Object.fromEntries(
        Object.entries(formData).filter(([_, v]) => v !== '')
      );

      const response = await adminApi.post(`/api/shipments`, cleanData);

      if (response.data.success) {
        toast.success(editingShipment ? 'Shipment updated' : 'Shipment created');
        setDialogOpen(false);
        resetForm();
        fetchShipments();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to save shipment';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Validation error - check all fields');
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;

    try {
      await adminApi.delete(`/api/shipments/${deleteConfirm.tracking_number}`);
      toast.success('Shipment deleted');
      setDeleteConfirm(null);
      fetchShipments();
    } catch (error) {
      toast.error('Failed to delete shipment');
    }
  };

  const filteredShipments = shipments.filter(s => {
    const query = searchQuery.toLowerCase();
    return (
      s.tracking_number?.toLowerCase().includes(query) ||
      s.container_number?.toLowerCase().includes(query) ||
      s.booking_number?.toLowerCase().includes(query) ||
      s.customer_name?.toLowerCase().includes(query)
    );
  });

  const getStatusInfo = (status) => STATUSES.find(s => s.value === status) || STATUSES[0];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-[#003d5c] to-[#004d70] text-white shadow-lg">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center">
              <Ship className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-xl font-bold">COZEY YILDIZ Admin</h1>
              <p className="text-sm text-blue-100">Business Management Portal</p>
            </div>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="bg-white/10 border-white/20 text-white hover:bg-white/20 hover:text-white"
            data-testid="admin-logout-button"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <Tabs defaultValue="shipments" className="w-full">
          <TabsList className="grid grid-cols-2 max-w-md mb-6 h-12">
            <TabsTrigger value="shipments" className="text-base" data-testid="tab-shipments">
              <Ship className="h-4 w-4 mr-2" />
              Shipments
            </TabsTrigger>
            <TabsTrigger value="inquiries" className="text-base" data-testid="tab-inquiries">
              <MessageSquare className="h-4 w-4 mr-2" />
              Inquiries
            </TabsTrigger>
          </TabsList>

          <TabsContent value="shipments">
        {/* Stats & Actions */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div>
            <h2 className="text-3xl font-bold text-[#003d5c]">Shipments</h2>
            <p className="text-gray-600 mt-1">Total: {shipments.length} shipments</p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={fetchShipments}
              variant="outline"
              disabled={loading}
              data-testid="refresh-button"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              onClick={() => handleOpenDialog()}
              className="bg-[#003d5c] hover:bg-[#002a40] text-white"
              data-testid="add-shipment-button"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Shipment
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <Input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by tracking number, container, booking, or customer name..."
            className="pl-12 h-12"
            data-testid="admin-search-input"
          />
        </div>

        {/* Shipments Table */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <Loader2 className="h-12 w-12 animate-spin text-[#003d5c] mx-auto" />
              <p className="mt-4 text-gray-600">Loading shipments...</p>
            </div>
          ) : filteredShipments.length === 0 ? (
            <div className="p-12 text-center">
              <Package className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 text-lg">
                {searchQuery ? 'No shipments match your search' : 'No shipments yet. Create your first one!'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left p-4 font-semibold text-[#003d5c]">Tracking #</th>
                    <th className="text-left p-4 font-semibold text-[#003d5c]">Customer</th>
                    <th className="text-left p-4 font-semibold text-[#003d5c]">Route</th>
                    <th className="text-left p-4 font-semibold text-[#003d5c]">Carrier</th>
                    <th className="text-left p-4 font-semibold text-[#003d5c]">Status</th>
                    <th className="text-left p-4 font-semibold text-[#003d5c]">Updated</th>
                    <th className="text-right p-4 font-semibold text-[#003d5c]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredShipments.map((shipment) => {
                    const statusInfo = getStatusInfo(shipment.status);
                    return (
                      <tr key={shipment.tracking_number} className="border-b hover:bg-gray-50 transition-colors">
                        <td className="p-4">
                          <p className="font-mono font-bold text-[#003d5c]">{shipment.tracking_number}</p>
                          {shipment.container_number && (
                            <p className="text-xs text-gray-500 mt-1">C: {shipment.container_number}</p>
                          )}
                        </td>
                        <td className="p-4">
                          <p className="font-medium">{shipment.customer_name || '-'}</p>
                          {shipment.customer_email && (
                            <p className="text-xs text-gray-500">{shipment.customer_email}</p>
                          )}
                        </td>
                        <td className="p-4">
                          <p className="text-sm">
                            <span className="text-gray-500">From:</span> {shipment.origin || '-'}
                          </p>
                          <p className="text-sm">
                            <span className="text-gray-500">To:</span> {shipment.destination || '-'}
                          </p>
                        </td>
                        <td className="p-4 text-sm">{shipment.carrier || '-'}</td>
                        <td className="p-4">
                          <span className={`inline-block px-3 py-1 rounded-full text-white text-xs font-semibold ${statusInfo.color}`}>
                            {statusInfo.label.length > 30 ? statusInfo.label.substring(0, 30) + '...' : statusInfo.label}
                          </span>
                        </td>
                        <td className="p-4 text-sm text-gray-600">
                          {shipment.updated_at ? new Date(shipment.updated_at).toLocaleDateString() : '-'}
                        </td>
                        <td className="p-4">
                          <div className="flex justify-end gap-2">
                            <Button
                              onClick={() => handleOpenDialog(shipment)}
                              size="sm"
                              variant="outline"
                              data-testid={`edit-${shipment.tracking_number}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              onClick={() => setDeleteConfirm(shipment)}
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:bg-red-50 hover:text-red-700"
                              data-testid={`delete-${shipment.tracking_number}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
          </TabsContent>

          <TabsContent value="inquiries">
            <InquiriesTab />
          </TabsContent>
        </Tabs>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl text-[#003d5c]">
              {editingShipment ? 'Update Shipment' : 'Create New Shipment'}
            </DialogTitle>
            <DialogDescription>
              {editingShipment
                ? 'Update the shipment status and details. Status change will be added to history.'
                : 'Fill in the shipment details to create a new tracking record.'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Tracking Number *</Label>
                <Input
                  value={formData.tracking_number}
                  onChange={(e) => setFormData({...formData, tracking_number: e.target.value.toUpperCase()})}
                  placeholder="COZY2026001"
                  required
                  disabled={!!editingShipment}
                  data-testid="form-tracking-number"
                />
              </div>
              <div>
                <Label>Container Number</Label>
                <Input
                  value={formData.container_number}
                  onChange={(e) => setFormData({...formData, container_number: e.target.value.toUpperCase()})}
                  placeholder="MSCU1234567"
                  data-testid="form-container-number"
                />
              </div>
              <div>
                <Label>Booking Number</Label>
                <Input
                  value={formData.booking_number}
                  onChange={(e) => setFormData({...formData, booking_number: e.target.value.toUpperCase()})}
                  placeholder="BK789456"
                />
              </div>
              <div>
                <Label>Customer Name</Label>
                <Input
                  value={formData.customer_name}
                  onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                  placeholder="Ahmed Al-Rashid"
                />
              </div>
              <div>
                <Label>Customer Email</Label>
                <Input
                  type="email"
                  value={formData.customer_email}
                  onChange={(e) => setFormData({...formData, customer_email: e.target.value})}
                  placeholder="customer@example.com"
                />
              </div>
              <div>
                <Label>Shipping Line (Carrier)</Label>
                <Input
                  value={formData.carrier}
                  onChange={(e) => setFormData({...formData, carrier: e.target.value})}
                  placeholder="MSC, CMA CGM, etc."
                />
              </div>
              <div>
                <Label>Port of Origin</Label>
                <Input
                  value={formData.origin}
                  onChange={(e) => setFormData({...formData, origin: e.target.value})}
                  placeholder="Qingdao, China"
                />
              </div>
              <div>
                <Label>Port of Destination</Label>
                <Input
                  value={formData.destination}
                  onChange={(e) => setFormData({...formData, destination: e.target.value})}
                  placeholder="Latakia, Syria"
                />
              </div>
              <div>
                <Label>Weight</Label>
                <Input
                  value={formData.weight}
                  onChange={(e) => setFormData({...formData, weight: e.target.value})}
                  placeholder="12,500 kg"
                />
              </div>
              <div>
                <Label>Volume (CBM)</Label>
                <Input
                  value={formData.volume}
                  onChange={(e) => setFormData({...formData, volume: e.target.value})}
                  placeholder="28 CBM"
                />
              </div>
              <div>
                <Label>Estimated Departure</Label>
                <Input
                  value={formData.est_departure}
                  onChange={(e) => setFormData({...formData, est_departure: e.target.value})}
                  placeholder="2026-07-20"
                />
              </div>
              <div>
                <Label>Estimated Arrival</Label>
                <Input
                  value={formData.est_arrival}
                  onChange={(e) => setFormData({...formData, est_arrival: e.target.value})}
                  placeholder="2026-08-15"
                />
              </div>
            </div>

            <div>
              <Label>Current Status *</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData({...formData, status: value})}
              >
                <SelectTrigger data-testid="form-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Location (Optional)</Label>
              <Input
                value={formData.location}
                onChange={(e) => setFormData({...formData, location: e.target.value})}
                placeholder="Mediterranean Sea, Suez Canal, etc."
              />
            </div>

            <div>
              <Label>Notes (Optional)</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Additional notes about this status update..."
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-[#003d5c] hover:bg-[#002a40] text-white"
                data-testid="form-submit-button"
              >
                {editingShipment ? 'Update Shipment' : 'Create Shipment'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Shipment?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete shipment <strong>{deleteConfirm?.tracking_number}</strong>?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminDashboard;
